import { useEffect, useRef, useState } from 'react';

// OGC-440: the one way to wait in BeyondLeads. A solid core with a ring that
// leaves it, widens and fades — a request went out, we are listening for the
// reply. One glyph at three sizes, coloured by currentColor, styled by
// `.bl-beacon` in theme.css (static core-and-rim under prefers-reduced-motion).
//
// Timing (all client-side, measured from mount):
// - `delayMs`: nothing visible for the first 300 ms, so reads that finish
//   inside it never flash an indicator. The markup is rendered hidden so
//   layout does not jump when it appears.
// - `minVisibleMs`: once visible, `active={false}` keeps the beacon on screen
//   until 500 ms have elapsed, so it never flickers. Callers that unmount the
//   beacon conditionally opt out of this by construction.
// - `slowAfterMs` / `slowText`: after 8 s a quiet second line acknowledges a
//   slow provider round trip. Pass `slowAfterMs={null}` for waits on our own
//   data; the line is only rendered under a visible label.

export const BEACON_SIZES = Object.freeze(['inline', 'control', 'page']);
export const BEACON_TONES = Object.freeze(['accent', 'neutral']);
export const BEACON_DEFAULT_DELAY_MS = 300;
export const BEACON_DEFAULT_MIN_VISIBLE_MS = 500;
export const BEACON_DEFAULT_SLOW_AFTER_MS = 8_000;
export const BEACON_SLOW_TEXT = 'Meta is taking longer than usual. This can take up to a minute.';
// Signed-media reads (posters, video previews) are our own store, not Meta.
export const BEACON_SLOW_TEXT_MEDIA = 'The media is taking longer than usual to prepare. This can take up to a minute.';

export function beaconSizeForPixels(px) {
  const n = Number(px);
  if (!Number.isFinite(n)) return 'control';
  if (n <= 16) return 'inline';
  if (n >= 30) return 'page';
  return 'control';
}

export function beaconClassName({ size = 'control', tone = 'accent', className = '' } = {}) {
  const exactSize = BEACON_SIZES.includes(size) ? size : 'control';
  const exactTone = BEACON_TONES.includes(tone) ? tone : 'accent';
  return [
    'bl-beacon',
    `is-${exactSize}`,
    exactTone === 'neutral' ? 'is-neutral' : '',
    className,
  ].filter(Boolean).join(' ');
}

function bounded(value, fallback) {
  if (value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

export function useBeaconPhase({ active = true, delayMs, minVisibleMs, slowAfterMs } = {}) {
  const delay = bounded(delayMs, BEACON_DEFAULT_DELAY_MS) ?? 0;
  const minVisible = bounded(minVisibleMs, BEACON_DEFAULT_MIN_VISIBLE_MS) ?? 0;
  const slowAfter = bounded(slowAfterMs, BEACON_DEFAULT_SLOW_AFTER_MS);
  const [visible, setVisible] = useState(delay === 0);
  const [slow, setSlow] = useState(false);
  const [holding, setHolding] = useState(false);
  const shownAt = useRef(null);

  // Delay: only while active. A request that finishes inside the delay never
  // shows anything (the deactivation effect below resets before the timer).
  useEffect(() => {
    if (!active) return undefined;
    if (visible) { if (shownAt.current === null) shownAt.current = Date.now(); return undefined; }
    const timer = setTimeout(() => { shownAt.current = Date.now(); setVisible(true); }, delay);
    return () => clearTimeout(timer);
  }, [active, delay, visible]);

  // Slow line: only while active and visible.
  useEffect(() => {
    if (!active || !visible || slowAfter === null) return undefined;
    const timer = setTimeout(() => setSlow(true), slowAfter);
    return () => clearTimeout(timer);
  }, [active, slowAfter, visible]);

  // Deactivation: hold a visible beacon to the minimum, then reset every
  // phase so the next activation starts clean (delay again, no slow line).
  useEffect(() => {
    if (active) return undefined;
    const reset = () => { setHolding(false); setSlow(false); setVisible(delay === 0); shownAt.current = null; };
    if (!visible || shownAt.current === null) { reset(); return undefined; }
    const remaining = minVisible - (Date.now() - shownAt.current);
    if (remaining <= 0) { reset(); return undefined; }
    setHolding(true);
    const timer = setTimeout(reset, remaining);
    return () => clearTimeout(timer);
  }, [active, delay, minVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Rendered at all: while active, or while holding for the minimum.
    mounted: active || holding,
    // Painted: after the delay has elapsed.
    visible,
    slow: visible && slow,
  };
}

export default function Beacon({
  size = 'control',
  tone = 'accent',
  label = '',
  slowText = BEACON_SLOW_TEXT,
  active = true,
  delayMs = BEACON_DEFAULT_DELAY_MS,
  minVisibleMs = BEACON_DEFAULT_MIN_VISIBLE_MS,
  slowAfterMs = BEACON_DEFAULT_SLOW_AFTER_MS,
  className = '',
  style = {},
  labelClassName = '',
}) {
  const phase = useBeaconPhase({ active, delayMs, minVisibleMs, slowAfterMs });
  if (!phase.mounted) return null;
  const text = String(label || '').trim();
  const hiddenStyle = phase.visible ? {} : { visibility: 'hidden' };
  const glyph = (
    <span
      className={beaconClassName({ size, tone, className: text ? '' : className })}
      aria-hidden="true"
      data-beacon-phase={phase.visible ? 'visible' : 'pending'}
      style={text ? undefined : { ...hiddenStyle, ...style }}
    >
      {size === 'page' ? <i /> : null}
    </span>
  );
  if (!text) return glyph;
  return (
    <span
      className={`bl-wait${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      data-beacon-phase={phase.visible ? 'visible' : 'pending'}
      style={{ ...hiddenStyle, ...style }}
    >
      {glyph}
      <span className={`bl-wait-text${labelClassName ? ` ${labelClassName}` : ''}`}>
        <span className="bl-wait-label">{text}</span>
        {phase.slow && String(slowText || '').trim() ? <span className="bl-wait-note">{slowText}</span> : null}
      </span>
    </span>
  );
}

// For regions that already own their live status text (page loaders with a
// title and body): the quiet second line on its own, after `slowAfterMs`.
export function BeaconSlowNote({ slowAfterMs = BEACON_DEFAULT_SLOW_AFTER_MS, text = BEACON_SLOW_TEXT, className = '' }) {
  const phase = useBeaconPhase({ active: true, delayMs: 0, minVisibleMs: 0, slowAfterMs });
  if (!phase.slow || !String(text || '').trim()) return null;
  return <span className={`bl-wait-note${className ? ` ${className}` : ''}`}>{text}</span>;
}
